import {
  buildOrderedPairMarketRefs,
  formatLegacyPoolId,
  formatPairLabel,
  formatPoolId,
  getPairCatalogSeed,
  getTokenCatalog,
  getTokenLegRole,
} from '@sirius-cybernetics-42/product-config'

import { getActiveChainId } from '@/config/active'
import { buildDisplayTokenRef } from '@/data/marketApi/normalizeMarketToken'
import type { PoolIdentity } from '@/data/marketApi/poolsTypes'
import { ChainId, Pair, Token } from '@/sdk'
import { wrappedCurrency } from '@/utils/wrappedCurrency'

import { currencyFromLegId } from './poolsIdentitySeedCurrency'

function symbolForCatalogAddress(chainId: ChainId, address: string): string {
  const entry = getTokenCatalog(chainId).find(
    (token) => token.catalogAddress.toLowerCase() === address.toLowerCase(),
  )
  return entry?.symbol ?? '?'
}

function pairAddressFromCatalogLegs(chainId: ChainId, token0Address: string, token1Address: string): string {
  const currencyA = currencyFromLegId(token0Address, chainId)
  const currencyB = currencyFromLegId(token1Address, chainId)
  if (!currencyA || !currencyB) return ''
  const wrappedA = wrappedCurrency(currencyA, chainId)
  const wrappedB = wrappedCurrency(currencyB, chainId)
  if (!wrappedA || !wrappedB || wrappedA.equals(wrappedB)) return ''
  if (!(wrappedA instanceof Token) || !(wrappedB instanceof Token)) return ''
  return Pair.getAddress(wrappedA, wrappedB)
}

/**
 * Identidad de pools planificados (id, legs, etiqueta) sin red.
 * Alineado con useTokenCatalog / usePairCatalog: product-config primero, API enriquece después.
 */
export function buildPoolsIdentitySeed(chainId: ChainId = getActiveChainId()): PoolIdentity[] {
  return getPairCatalogSeed(chainId).map((pair) => {
    const token0Symbol = symbolForCatalogAddress(chainId, pair.token0Address)
    const token1Symbol = symbolForCatalogAddress(chainId, pair.token1Address)
    const token0 = buildDisplayTokenRef(pair.token0Address, token0Symbol, chainId)
    const token1 = buildDisplayTokenRef(pair.token1Address, token1Symbol, chainId)
    const ordered = buildOrderedPairMarketRefs(
      pair.token0Address,
      token0Symbol,
      token0Symbol,
      pair.token1Address,
      token1Symbol,
      token1Symbol,
      chainId,
    )
    const id = formatPoolId(
      pair.token0Address,
      token0Symbol,
      pair.token1Address,
      token1Symbol,
      chainId,
    )
    const legacyId = formatLegacyPoolId(
      pair.token0Address,
      token0Symbol,
      pair.token1Address,
      token1Symbol,
      chainId,
    )
    const pairAddress = pairAddressFromCatalogLegs(chainId, pair.token0Address, pair.token1Address)

    return {
      id,
      legacyId,
      pair: formatPairLabel(
        pair.token0Address,
        token0Symbol,
        pair.token1Address,
        token1Symbol,
        chainId,
      ),
      pairAddress,
      token0: { ...token0, role: token0.role ?? getTokenLegRole(pair.token0Address, chainId) },
      token1: { ...token1, role: token1.role ?? getTokenLegRole(pair.token1Address, chainId) },
      baseToken: ordered.base,
      quoteToken: ordered.quote,
      swapEnabled: pair.swapEnabled,
      addLiquidityEnabled: pair.addLiquidityEnabled,
      removeLiquidityEnabled: pair.removeLiquidityEnabled,
      maxSwapPriceImpactBps: pair.maxSwapPriceImpactBps,
    }
  })
}
